// index.tsx
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useDebounce } from 'use-debounce';

import { AppRouteBody } from '@/components/AppRouteBody';
import { FilterDropdown } from '@/components/filters/FilterDropdown';
import { Loader } from '@/components/Loader';
import { Table } from '@/components/table/Table';
import { SearchBar } from '@/components/ui/SearchBar';
import { useKEVVulnerabilities } from '@/hooks/useKEVVulnerabilities';
import { useGlobalState } from '@/state/global.state';

import { Row } from './components';

const KEV_FILTER_OPTIONS = [
  { label: 'Yes', value: 'true', id: 'yes' },
  { label: 'No', value: 'false', id: 'no' },
];

export function ThreatIntelComponent() {
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 500);
  const [kevFilter, setKevFilter] = useState<string[]>(['true']);
  const { drawerState } = useGlobalState();
  const [searchParams] = useSearchParams();

  // Handle URL parameters for deep linking
  useEffect(() => {
    const drawerKey = searchParams.get('threatIntelDrawerKey');
    const drawerOrder = searchParams.get('drawerOrder');

    if (drawerKey && drawerOrder) {
      // Global state handles drawer opening from URL params
    }
  }, [searchParams]);

  const showKevOnly =
    kevFilter.includes('true') && !kevFilter.includes('false');
  const showNonKevOnly =
    kevFilter.includes('false') && !kevFilter.includes('true');

  const { data, status, error } = useKEVVulnerabilities({
    search: debouncedSearch,
    limit: 100,
    kevFilter: showKevOnly ? true : showNonKevOnly ? false : undefined,
  });

  const vulnerabilities = data?.vulnerabilities ?? [];

  return (
    <AppRouteBody title="Threats">
      <div className="flex items-center gap-4 pb-4">
        <SearchBar
          size="sm"
          className="h-fit w-64 shrink-0 pr-2"
          placeholder="CVE-1999-0192"
          value={search}
          onChange={setSearch}
        />
        <FilterDropdown
          id="kev-filter"
          label="KEV"
          options={KEV_FILTER_OPTIONS}
          selectedValues={kevFilter}
          onApply={options =>
            setKevFilter(options.map(o => String(o.value)).filter(Boolean))
          }
          onClear={() => setKevFilter([])}
          width={200}
        />
      </div>

      <div className="flex flex-col gap-4">
        {[...new Array(6)].map((_, index) => (
          <Loader
            className="h-32"
            key={index}
            isLoading={status === 'pending'}
          />
        ))}
      </div>

      <Table
        keyboardNavigation
        name="threatIntel"
        hideTableHeader
        columns={[
          {
            label: '',
            id: 'id',
            cell: row => <Row row={row} />,
            isExpandable: { minWidth: 800 },
          },
        ]}
        data={vulnerabilities}
        onRowClick={row => drawerState.handleOpenThreatIntelDrawer(row)}
        status={status}
        error={error}
        rowHeight={142}
        selection={{ hide: true }}
      />
    </AppRouteBody>
  );
}

// Re-export ThreatTag for backward compatibility
export { ThreatTag } from './components';
